/**
 * Actor: a 4-direction animated map character (32x48 sprite, origin (0.5, 1) = feet baseline,
 * feet collision box 20x12). Movement is simulated on the fixed step through ActorSystem; the sprite
 * is always snapped to integer pixels and depth = Depth.WORLD_BASE + feetY.
 *
 * Coordinates given to walkTo/faceTowards/setPosition are WORLD PIXELS (same space as MapSpawn).
 *
 * Animation states: the contract's `ActorAnimState` plus any art-specific state on the atlas
 * (`ponder`, `laugh`, `drink` …). A state the art lacks resolves through the fallback chain
 * `ActorDef.fallback` / atlas `meta.fallback` → `DEFAULT_STATE_FALLBACK` → idle (赵呈博 `write` → sit).
 *
 * Emotes (`showEmote`): a 16 × 16 bubble 52 px above the feet from the UI emote atlas `ui-emote-bubbles` —
 * frame `<name>`, else the aliased icon (`EMOTE_ALIAS`, emotes.ts), else a standalone `emote-<name>` texture,
 * else the DEV bubble `dev-emote-<name>`. `currentEmote` reports what is showing.
 *
 * Lifecycle: destroyed on scene SHUTDOWN/DESTROY as well as explicitly; pending walkTo()/showEmote()
 * promises always resolve (cancel, arrival, destroy), so timelines never hang on a stopped scene.
 */
import type Phaser from 'phaser';
import { Depth, TILE } from '@/core/config';
import type { IActor } from '@/core/types/contracts';
import type { Facing } from '@/core/types';
import { ActorSystem, StepOrder, type Steppable } from './ActorSystem';
import { getActorDef, type ActorDef } from './actorDefs';
import { animKey, atlasMeta, ensureActorAnims, frameName, resolveStateChain, type ActorState } from './anims';
import { ensureDevActorTexture, ensureDevEmoteTexture } from './devTextures';
import { resolveEmote, type EmoteLookup } from './emotes';
import { FEET_BOX } from './feet';
import { bfsPath, lineClear, nearestWalkableTile, pullString, simplifyPath, type GridPoint } from './pathfinding';
import { SceneEvents } from './sceneEvents';

export { FEET_BOX } from './feet';

export interface ActorOptions {
  /** World-pixel collision query (from the map runtime). Default: nothing is blocked. */
  isBlocked?: (px: number, py: number) => boolean;
  /**
   * Optional actor–actor blocking: true when `self` standing with its feet at (fx, fy) would overlap
   * another actor (see `feetOverlap`). Consulted by movement only (controller, walks); path planning
   * and follower placement stay map-only, and a walk blocked by an actor resolves via the stuck guard.
   */
  isActorBlocked?: (self: Actor, fx: number, fy: number) => boolean;
  /** Map size in world pixels; bounds the BFS grid and clamps positions. Default 8192x8192 grid extent, no clamp. */
  bounds?: { w: number; h: number };
  /** Walk speed in px/s (default: ACTOR_DEFS speed or 80). */
  speed?: number;
  tileSize?: number;
}

const STUCK_STEPS = 15;
const LINE_SAMPLE_PX = 4;

type Mode = 'idle' | 'path' | 'velocity' | 'driven';

interface WalkJob { waypoints: { x: number; y: number }[]; idx: number; speed: number; resolve: () => void; stuck: number }
interface EmoteJob { icon: Phaser.GameObjects.Image; name: string; texture: string; frame: string | null; remaining: number; elapsed: number; resolve: () => void }
/** Emote bubble currently shown by an actor (after alias resolution); `frame` is null for a standalone image. */
export interface ShownEmote { name: string; texture: string; frame: string | null }

/** One warning per `<texture>:<state>` for the whole session (never per facing, never per actor instance). */
const missingAnimWarned = new Set<string>();

/** Real texture key when loaded → its aliases → preloaded dev atlas (same frame names) → procedural placeholder (`dev-proc-<actorKey>`). */
function resolveActorTexture(scene: Phaser.Scene, def: ActorDef, actorKey: string): string {
  if (scene.textures.exists(def.texture)) return def.texture;
  for (const alias of def.textureAliases ?? []) if (scene.textures.exists(alias)) return alias;
  if (def.devAtlas && scene.textures.exists(def.devAtlas)) return def.devAtlas;
  return ensureDevActorTexture(scene, actorKey);
}

export class Actor implements IActor, Steppable {
  readonly sprite: Phaser.GameObjects.Sprite;
  readonly def: ActorDef;
  readonly texture: string;
  readonly speed: number;
  /** Effective state fallbacks: atlas `meta.fallback` overridden by `ActorDef.fallback`. */
  readonly fallback: Readonly<Record<string, string>>;
  private fxPos: number;
  private fyPos: number;
  private facingValue: Facing;
  private baseState: ActorState = 'idle';
  private moving = false;
  private mode: Mode = 'idle';
  private walk: WalkJob | null = null;
  private vel: { x: number; y: number } | null = null;
  private emoteJob: EmoteJob | null = null;
  private controllable = false;
  private currentAnim = '';
  private destroyed = false;
  private altTexture: string | null | undefined;
  private readonly tileSize: number;
  private readonly system: ActorSystem;
  private readonly isBlockedPx: (px: number, py: number) => boolean;
  private readonly onShutdown = () => this.destroy();
  private readonly emoteLookup: EmoteLookup = {
    hasFrame: (t, f) => this.scene.textures.exists(t) && this.scene.textures.get(t).has(f),
    hasTexture: (k) => this.scene.textures.exists(k),
  };

  constructor(readonly scene: Phaser.Scene, readonly id: string, readonly actorKey: string, x: number, y: number, facing: Facing, private readonly opts: ActorOptions = {}) {
    this.def = getActorDef(actorKey);
    this.texture = resolveActorTexture(scene, this.def, actorKey);
    this.tileSize = opts.tileSize ?? TILE;
    this.speed = opts.speed ?? this.def.speed ?? 80;
    this.isBlockedPx = opts.isBlocked ?? (() => false);
    ensureActorAnims(scene, this.texture);
    const tex = scene.textures.get(this.texture);
    this.fallback = { ...atlasMeta(tex).fallback, ...this.def.fallback };
    const first = frameName('idle', facing, 0);
    this.sprite = scene.add.sprite(Math.round(x), Math.round(y), this.texture, tex.has(first) ? first : undefined).setOrigin(0.5, 1);
    this.fxPos = x; this.fyPos = y;
    this.facingValue = facing;
    this.syncSprite();
    this.applyAnim(true);
    this.system = ActorSystem.get(scene);
    this.system.add(this, StepOrder.ACTOR);
    scene.events.once(SceneEvents.SHUTDOWN, this.onShutdown);
    scene.events.once(SceneEvents.DESTROY, this.onShutdown);
  }

  // ------------------------------------------------------------------ position / facing
  get x(): number { return this.sprite.x; }
  set x(v: number) { this.fxPos = v; this.syncSprite(); }
  get y(): number { return this.sprite.y; }
  set y(v: number) { this.fyPos = v; this.syncSprite(); }
  /** Sub-pixel simulation position (rendering uses the rounded sprite position). */
  get fx(): number { return this.fxPos; }
  get fy(): number { return this.fyPos; }
  get facing(): Facing { return this.facingValue; }
  set facing(f: Facing) { this.setFacing(f); }
  /** Requested state (`walk` while moving); the animation actually playing may be a fallback (see `currentAnimKey`). */
  get state(): ActorState { return this.moving ? 'walk' : this.baseState; }
  /** Animation key currently playing (after fallback resolution); '' when holding a static frame. */
  get currentAnimKey(): string { return this.sprite.anims.currentAnim?.key ?? ''; }
  /** Emote bubble showing right now (name as requested, texture/frame it resolved to), or null. */
  get currentEmote(): ShownEmote | null { const e = this.emoteJob; return e ? { name: e.name, texture: e.texture, frame: e.frame } : null; }
  get displayName(): string { return this.def.displayName; }
  isDestroyed(): boolean { return this.destroyed; }

  setFacing(f: Facing): void {
    if (this.facingValue === f) return;
    this.facingValue = f;
    this.applyAnim();
  }

  faceTowards(x: number, y: number): void {
    const dx = x - this.fxPos, dy = y - this.fyPos;
    if (dx === 0 && dy === 0) return;
    this.setFacing(Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : (dy > 0 ? 'down' : 'up'));
  }

  /** Teleport (cancels any walk). */
  setPosition(x: number, y: number, facing?: Facing): void {
    this.cancelWalk();
    this.vel = null;
    this.fxPos = x; this.fyPos = y;
    if (facing) this.facingValue = facing;
    this.setMoving(false);
    this.syncSprite();
  }

  // ------------------------------------------------------------------ animation
  /** Any contract state or art state on the atlas (`ponder` …); missing states resolve through the fallback chain. */
  play(state: ActorState, emote?: string): void {
    this.baseState = state;
    if (state === 'emote' && emote) void this.showEmote(emote);
    this.applyAnim(true);
  }

  private applyAnim(force = false): void {
    if (this.destroyed) return;
    const st = this.state;
    const key = animKey(this.texture, st, this.facingValue);
    if (!force && key === this.currentAnim) return;
    this.currentAnim = key;
    const anims = this.scene.anims;
    if (anims.exists(key)) { this.sprite.play(key, true); return; }
    // DEV ONLY: a preloaded dev atlas carries walk + idle only → borrow the state from the procedural sheet.
    const alt = this.devAltTexture();
    if (alt) {
      const altKey = animKey(alt, st, this.facingValue);
      if (anims.exists(altKey)) { this.sprite.play(altKey, true); return; }
    }
    const chain = resolveStateChain(st, this.fallback);
    for (const s of chain.slice(1)) {
      const k = animKey(this.texture, s, this.facingValue);
      if (anims.exists(k)) { this.warnFallback(st, s); this.sprite.play(k, true); return; }
    }
    this.warnFallback(st, null);
    const tex = this.scene.textures.get(this.texture);
    this.sprite.anims.stop();
    for (const name of [...chain.map((s) => frameName(s, this.facingValue, 0)), frameName('idle', 'down', 0)]) {
      if (tex.has(name)) { this.sprite.setTexture(this.texture, name); return; }
    }
  }

  /** Procedural sheet used only when the actor is drawn from a dev atlas (created lazily, once). */
  private devAltTexture(): string | null {
    if (this.altTexture !== undefined) return this.altTexture;
    const onDevAtlas = this.texture !== this.def.texture && this.texture === this.def.devAtlas;
    this.altTexture = onDevAtlas ? ensureDevActorTexture(this.scene, this.actorKey) : null;
    if (this.altTexture) ensureActorAnims(this.scene, this.altTexture);
    return this.altTexture;
  }

  /** Once per `<texture>:<state>`; silent for `emote` (the bubble is the emote) and for declared per-actor fallbacks (by design). */
  private warnFallback(state: string, used: string | null): void {
    if (state === 'emote') return;
    if (used && this.fallback[state] !== undefined) return;
    const k = `${this.texture}:${state}`;
    if (missingAnimWarned.has(k)) return;
    missingAnimWarned.add(k);
    console.warn(`[characters] ${this.texture} has no "${state}" animation; ${used ? `playing "${used}" instead` : 'holding a static frame'}`);
  }

  private setMoving(m: boolean): void {
    if (this.moving === m) return;
    this.moving = m;
    if (!m && this.baseState === 'walk') this.baseState = 'idle';
    this.applyAnim();
  }

  private faceFromDelta(dx: number, dy: number): void {
    if (dx === 0 && dy === 0) return;
    const ax = Math.abs(dx), ay = Math.abs(dy);
    let f: Facing;
    if (ax > ay + 1e-6) f = dx > 0 ? 'right' : 'left';
    else if (ay > ax + 1e-6) f = dy > 0 ? 'down' : 'up';
    else { // exact diagonal: keep the current facing when it is compatible with the motion
      const h: Facing = dx > 0 ? 'right' : 'left', v: Facing = dy > 0 ? 'down' : 'up';
      f = this.facingValue === v ? v : h;
    }
    this.setFacing(f);
  }

  // ------------------------------------------------------------------ collision
  /** True when the 20x12 feet box centred at (fx, fy - 6) touches a blocked MAP pixel (actors excluded). */
  isBlockedAt(fx: number, fy: number): boolean {
    const l = fx - FEET_BOX.w / 2, r = fx + FEET_BOX.w / 2 - 1, t = fy - FEET_BOX.h, b = fy - 1;
    const q = this.isBlockedPx;
    return q(l, t) || q(r, t) || q(l, b) || q(r, b) || q(fx, t) || q(fx, b);
  }

  /** Map blocking plus the optional actor–actor query (movement only). */
  private moveBlocked(fx: number, fy: number): boolean {
    return this.isBlockedAt(fx, fy) || (this.opts.isActorBlocked?.(this, fx, fy) ?? false);
  }

  /** Axis-separated move with pixel-granular sliding; returns actual displacement. */
  private tryMove(dx: number, dy: number): { dx: number; dy: number } {
    const sx = this.fxPos, sy = this.fyPos;
    if (dx !== 0) {
      if (!this.moveBlocked(this.fxPos + dx, this.fyPos)) this.fxPos += dx;
      else { const s = Math.sign(dx); let m = 0; while (Math.abs(m + s) <= Math.abs(dx) && !this.moveBlocked(this.fxPos + s, this.fyPos)) { this.fxPos += s; m += s; } }
    }
    if (dy !== 0) {
      if (!this.moveBlocked(this.fxPos, this.fyPos + dy)) this.fyPos += dy;
      else { const s = Math.sign(dy); let m = 0; while (Math.abs(m + s) <= Math.abs(dy) && !this.moveBlocked(this.fxPos, this.fyPos + s)) { this.fyPos += s; m += s; } }
    }
    this.clampToBounds();
    return { dx: this.fxPos - sx, dy: this.fyPos - sy };
  }

  private clampToBounds(): void {
    const b = this.opts.bounds;
    if (!b) return;
    this.fxPos = Math.min(Math.max(this.fxPos, FEET_BOX.w / 2), b.w - FEET_BOX.w / 2);
    this.fyPos = Math.min(Math.max(this.fyPos, FEET_BOX.h), b.h);
  }

  // ------------------------------------------------------------------ movement API
  /** Controller-driven movement for the next fixed step (px/s). Cancels any path walk. */
  setVelocity(vx: number, vy: number): void {
    if (this.walk) this.cancelWalk();
    this.vel = { x: vx, y: vy };
    this.mode = 'velocity';
  }

  /** Follower-driven: position is applied immediately, collision is not re-evaluated. */
  setPositionDriven(x: number, y: number, moving: boolean, facing?: Facing): void {
    if (this.walk) this.cancelWalk();
    this.vel = null;
    this.mode = 'driven';
    this.fxPos = x; this.fyPos = y;
    if (facing) this.setFacing(facing);
    this.setMoving(moving);
    this.syncSprite();
  }
  releaseDriven(): void { if (this.mode === 'driven') { this.mode = 'idle'; this.setMoving(false); } }

  /** True while a path walk is active, while controller velocity actually displaces the actor (not when pinned against a wall), or while a follower drives it. */
  isMoving(): boolean { return this.walk !== null || this.moving; }

  walkTo(x: number, y: number, speedPxPerSec?: number): Promise<void> {
    this.cancelWalk();
    this.vel = null;
    if (this.destroyed) return Promise.resolve();
    const ts = this.tileSize;
    const gridW = this.opts.bounds ? Math.ceil(this.opts.bounds.w / ts) : 256;
    const gridH = this.opts.bounds ? Math.ceil(this.opts.bounds.h / ts) : 256;
    const tileBlocked = (tx: number, ty: number) => tx < 0 || ty < 0 || tx >= gridW || ty >= gridH || this.isBlockedPx(tx * ts + ts / 2, ty * ts + ts / 2);
    const toTile = (px: number, py: number): GridPoint => ({ x: Math.floor(px / ts), y: Math.floor(py / ts) });
    const centre = (t: GridPoint) => ({ x: t.x * ts + ts / 2, y: t.y * ts + ts / 2 });
    const clear = (a: { x: number; y: number }, b: { x: number; y: number }) => lineClear(a.x, a.y, b.x, b.y, LINE_SAMPLE_PX, (px, py) => this.isBlockedAt(px, py));

    let goal = { x, y };
    if (this.isBlockedAt(goal.x, goal.y)) {
      const nt = nearestWalkableTile(tileBlocked, gridW, gridH, toTile(x, y));
      if (!nt) return Promise.resolve();
      goal = centre(nt);
    }
    const here = { x: this.fxPos, y: this.fyPos };
    let waypoints: { x: number; y: number }[];
    if (clear(here, goal)) waypoints = [goal];
    else {
      const res = bfsPath(tileBlocked, gridW, gridH, toTile(here.x, here.y), toTile(goal.x, goal.y));
      const pts = simplifyPath(res.path).map(centre);
      if (res.complete) {
        const last = pts[pts.length - 1];
        if (!last) pts.push(goal);
        else if (clear(last, goal)) pts[pts.length - 1] = goal;
        else pts.push(goal);
      }
      waypoints = pullString([here, ...pts], clear).slice(1);
    }
    if (waypoints.length === 0) return Promise.resolve();
    return new Promise<void>((resolve) => {
      this.walk = { waypoints, idx: 0, speed: speedPxPerSec ?? this.speed, resolve, stuck: 0 };
      this.mode = 'path';
      const first = waypoints[0] as { x: number; y: number };
      this.faceFromDelta(first.x - this.fxPos, first.y - this.fyPos);
      this.setMoving(true);
    });
  }

  stop(): void {
    this.cancelWalk();
    this.vel = null;
    if (this.mode !== 'driven') { this.mode = 'idle'; this.setMoving(false); }
  }

  private cancelWalk(): void {
    if (!this.walk) return;
    const w = this.walk;
    this.walk = null;
    if (this.mode === 'path') this.mode = 'idle';
    w.resolve();
  }

  private finishWalk(): void {
    const w = this.walk;
    this.walk = null;
    this.mode = 'idle';
    this.setMoving(false);
    w?.resolve();
  }

  // ------------------------------------------------------------------ fixed step
  step(dtMs: number): void {
    if (this.destroyed) return;
    if (this.walk) this.stepWalk(dtMs);
    else if (this.mode === 'velocity') this.stepVelocity(dtMs);
    else if (this.mode === 'idle' && this.moving) this.setMoving(false); // velocity not re-applied this step → stop
    this.syncSprite();
    this.stepEmote(dtMs);
  }

  private stepWalk(dtMs: number): void {
    const w = this.walk as WalkJob;
    let budget = (w.speed * dtMs) / 1000;
    for (let guard = 0; guard < 4 && budget > 0 && this.walk; guard++) {
      const wp = w.waypoints[w.idx];
      if (!wp) { this.finishWalk(); return; }
      const dx = wp.x - this.fxPos, dy = wp.y - this.fyPos;
      const dist = Math.hypot(dx, dy);
      if (dist <= budget) {
        const moved = this.tryMove(dx, dy);
        budget -= dist;
        if (Math.hypot(moved.dx, moved.dy) < dist * 0.5) { this.finishWalk(); return; }
        w.idx++;
        if (w.idx >= w.waypoints.length) { this.finishWalk(); return; }
        continue;
      }
      const mx = (dx / dist) * budget, my = (dy / dist) * budget;
      this.faceFromDelta(dx, dy);
      const moved = this.tryMove(mx, my);
      const actual = Math.hypot(moved.dx, moved.dy);
      if (actual < budget * 0.25) w.stuck++; else w.stuck = 0;
      if (w.stuck >= STUCK_STEPS) { this.finishWalk(); return; }
      budget = 0;
    }
  }

  private stepVelocity(dtMs: number): void {
    const v = this.vel;
    this.vel = null; // velocity must be re-applied every step by the controller
    this.mode = 'idle';
    if (!v || (v.x === 0 && v.y === 0)) { this.setMoving(false); return; }
    this.faceFromDelta(v.x, v.y); // always turn toward the input, even into a wall
    const moved = this.tryMove((v.x * dtMs) / 1000, (v.y * dtMs) / 1000);
    this.setMoving(moved.dx !== 0 || moved.dy !== 0); // pinned against a wall → idle, no walk-in-place
  }

  private syncSprite(): void {
    const sx = Math.round(this.fxPos), sy = Math.round(this.fyPos);
    if (this.sprite.x !== sx || this.sprite.y !== sy) this.sprite.setPosition(sx, sy);
    this.sprite.setDepth(Depth.WORLD_BASE + sy);
    if (this.emoteJob) this.placeEmote();
  }

  // ------------------------------------------------------------------ emotes
  /** Shows the bubble for `ms` (≤ 0 = until hideEmote/destroy). Resolves when it hides. */
  showEmote(emote: string, ms = 1200): Promise<void> {
    if (this.destroyed) return Promise.resolve();
    this.hideEmote();
    const ref = resolveEmote(emote, this.emoteLookup) ?? { texture: ensureDevEmoteTexture(this.scene, emote), frame: null };
    const icon = this.scene.add.image(this.sprite.x, this.sprite.y - 52, ref.texture, ref.frame ?? undefined).setOrigin(0.5, 1);
    return new Promise<void>((resolve) => {
      this.emoteJob = { icon, name: emote, texture: ref.texture, frame: ref.frame, remaining: ms > 0 ? ms : Infinity, elapsed: 0, resolve };
      this.placeEmote();
    });
  }

  hideEmote(): void {
    const e = this.emoteJob;
    if (!e) return;
    this.emoteJob = null;
    e.icon.destroy();
    e.resolve();
  }

  private placeEmote(): void {
    const e = this.emoteJob;
    if (!e) return;
    const bob = Math.round(Math.sin(e.elapsed / 160) * 2);
    e.icon.setPosition(this.sprite.x, this.sprite.y - 52 + bob).setDepth(Depth.WORLD_BASE + this.sprite.y + 1);
  }

  private stepEmote(dtMs: number): void {
    const e = this.emoteJob;
    if (!e) return;
    e.elapsed += dtMs;
    e.remaining -= dtMs;
    if (e.remaining <= 0) this.hideEmote(); else this.placeEmote();
  }

  // ------------------------------------------------------------------ misc
  setControllable(on: boolean): void { this.controllable = on; }
  isControllable(): boolean { return this.controllable; }

  /** Idempotent; also runs on scene SHUTDOWN/DESTROY. Resolves any pending walkTo()/showEmote() promise. */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.scene.events.off(SceneEvents.SHUTDOWN, this.onShutdown);
    this.scene.events.off(SceneEvents.DESTROY, this.onShutdown);
    this.cancelWalk();
    this.hideEmote();
    this.system.remove(this);
    this.sprite.destroy();
  }
}
